// Parses a Slido-exported slide HTML file into a flat list of semantic elements
// with absolute coordinates in the 960×700 slide space.
// Used to populate embed shape meta so the spaces AI agent can read slide content.
//
// Fragment index (fragmentIndex) = order in which elements appear as teacher
// clicks through the slide. Undefined means always visible.

export type SlideElement =
  | { type: "text";  x1: number; y1: number; x2: number; y2: number; text: string;           fragmentIndex?: number }
  | { type: "math";  x1: number; y1: number; x2: number; y2: number; latex: string;          fragmentIndex?: number }
  | { type: "image"; x1: number; y1: number; x2: number; y2: number;                         fragmentIndex?: number }
  | { type: "input"; x1: number; y1: number; x2: number; y2: number;                         fragmentIndex?: number }
  | { type: "svg";   x1: number; y1: number; x2: number; y2: number; desc: string;           fragmentIndex?: number }

function parseInlineStyle(el: Element): { left: number; top: number; width: number; height: number } {
  const s = (el as HTMLElement).style
  const parse = (v: string) => parseFloat(v) || 0
  return { left: parse(s.left), top: parse(s.top), width: parse(s.width), height: parse(s.height) }
}

function fragmentIndex(el: Element): number | undefined {
  const cls = (el as HTMLElement).className ?? ""
  if (!cls.includes("fragment")) return undefined
  const raw = el.getAttribute("data-fragment-index")
  if (raw === null) return undefined
  const n = parseInt(raw)
  return isNaN(n) ? undefined : n
}

// ---------------------------------------------------------------------------
// SVG description builder
// ---------------------------------------------------------------------------

function describeSvg(svgEl: SVGSVGElement, blockW: number, blockH: number): string {
  const parts: string[] = []

  const walk = (node: Element) => {
    if (node.tagName.toLowerCase() === "defs") return
    for (const child of Array.from(node.children)) {
      describeNode(child)
      walk(child)
    }
  }

  const describeNode = (el: Element) => {
    const tag = el.tagName.toLowerCase()

    if (tag === "path") {
      if (el.closest("defs")) return
      const hasStart = el.getAttribute("marker-start")
      const hasEnd = el.getAttribute("marker-end")
      const d = el.getAttribute("d") ?? ""
      const fill = el.getAttribute("fill") ?? "none"
      const dasharray = el.getAttribute("stroke-dasharray") ?? "none"
      const dashed = dasharray !== "none" && dasharray !== "" ? "(dashed)" : ""

      if (hasStart || hasEnd) {
        const ends = [hasStart && "start", hasEnd && "end"].filter(Boolean).join("+")
        parts.push(`arrow(${ends})${dashed}`)
      } else if (fill !== "none" && fill !== "") {
        parts.push("filled-path")
      } else {
        const nums = d.match(/-?\d+(\.\d+)?/g)?.map(Number) ?? []
        if (nums.length >= 4) {
          const xs = nums.filter((_, i) => i % 2 === 0)
          const ys = nums.filter((_, i) => i % 2 === 1)
          const pw = Math.max(...xs) - Math.min(...xs)
          const ph = Math.max(...ys) - Math.min(...ys)
          if (blockW <= 12 && blockH <= 12) parts.push("dot")
          else if (pw >= ph * 2) parts.push(`hline${dashed}`)
          else if (ph >= pw * 2) parts.push(`vline${dashed}`)
          else parts.push(`curve${dashed}`)
        } else {
          parts.push(`line${dashed}`)
        }
      }
    } else if (tag === "line") {
      const x1 = parseFloat(el.getAttribute("x1") ?? "0")
      const y1 = parseFloat(el.getAttribute("y1") ?? "0")
      const x2 = parseFloat(el.getAttribute("x2") ?? "0")
      const y2 = parseFloat(el.getAttribute("y2") ?? "0")
      const dx = Math.abs(x2 - x1), dy = Math.abs(y2 - y1)
      if (dx >= dy * 2) parts.push("hline")
      else if (dy >= dx * 2) parts.push("vline")
      else parts.push("diagonal-line")
    } else if (tag === "rect") {
      const w = parseFloat(el.getAttribute("width") ?? "0")
      const h = parseFloat(el.getAttribute("height") ?? "0")
      const rx = parseFloat(el.getAttribute("rx") ?? "0")
      parts.push(rx > 0 ? `rounded-rect(${Math.round(w)}x${Math.round(h)})` : `rect(${Math.round(w)}x${Math.round(h)})`)
    } else if (tag === "circle" || tag === "ellipse") {
      const r = tag === "circle"
        ? parseFloat(el.getAttribute("r") ?? "0")
        : (parseFloat(el.getAttribute("rx") ?? "0") + parseFloat(el.getAttribute("ry") ?? "0")) / 2
      parts.push(`circle(r≈${Math.round(r)})`)
    } else if (tag === "polygon" || tag === "polyline") {
      parts.push(tag)
    } else if (tag === "text" || tag === "tspan") {
      const t = (el.textContent ?? "").trim()
      if (t) parts.push(`svg-text:"${t}"`)
    } else if (tag === "image") {
      parts.push("svg-image")
    }
  }

  walk(svgEl)

  if (parts.length === 0) return "svg"

  // Deduplicate consecutive identical tokens: vline, vline, vline → vline×3
  const deduped: string[] = []
  for (const p of parts) {
    const last = deduped[deduped.length - 1]
    if (typeof last === "string" && last === p) {
      deduped[deduped.length - 1] = `${p}×2`
    } else if (typeof last === "string" && last.startsWith(`${p}×`)) {
      const n = parseInt(last.split("×")[1])
      deduped[deduped.length - 1] = `${p}×${n + 1}`
    } else {
      deduped.push(p)
    }
  }
  return deduped.join(", ")
}

// ---------------------------------------------------------------------------
// Main element extractor
// ---------------------------------------------------------------------------

function extractElements(root: Element, offsetX = 0, offsetY = 0, parentFragIdx?: number): SlideElement[] {
  const results: SlideElement[] = []

  for (const el of Array.from(root.children)) {
    const cls = (el as HTMLElement).className ?? ""

    if (cls.includes("sl-block-group")) {
      const pos = parseInlineStyle(el)
      // Fragment index propagates from group to children (children don't get their own fragment class)
      const fragIdx = fragmentIndex(el) ?? parentFragIdx
      results.push(...extractElements(el, offsetX + pos.left, offsetY + pos.top, fragIdx))
      continue
    }

    if (!cls.includes("sl-block")) continue

    const pos = parseInlineStyle(el)
    const x1 = Math.round(offsetX + pos.left)
    const y1 = Math.round(offsetY + pos.top)
    const x2 = Math.round(x1 + pos.width)
    const y2 = Math.round(y1 + pos.height)
    const fragIdx = fragmentIndex(el) ?? parentFragIdx
    const frag = fragIdx !== undefined ? { fragmentIndex: fragIdx } : {}

    // Input element (interactive answer field)
    if (el.getAttribute("data-element-type") === "input") {
      results.push({ type: "input", x1, y1, x2, y2, ...frag })
      continue
    }

    // LaTeX / math
    const mathEl = el.querySelector(".math-input")
    if (mathEl) {
      const latex = (mathEl.textContent ?? "").trim()
      if (latex) results.push({ type: "math", x1, y1, x2, y2, latex, ...frag })
      continue
    }

    // SVG
    const svgEl = el.querySelector("svg")
    if (svgEl) {
      const desc = describeSvg(svgEl as unknown as SVGSVGElement, pos.width, pos.height)
      results.push({ type: "svg", x1, y1, x2, y2, desc, ...frag })
      continue
    }

    // Image via background-image — renderer emits a content div inside a wrapper div
    const bgImageEl = el.querySelector('[style*="background-image"]') as HTMLElement | null
    if (bgImageEl?.style.backgroundImage) {
      results.push({ type: "image", x1, y1, x2, y2, ...frag })
      continue
    }

    // CSS rect/shape — content div is el > div > div (wrapper then content)
    if (!(el.textContent ?? "").trim()) {
      const contentDiv = el.querySelector("div > div") as HTMLElement | null
      const border = contentDiv?.style.border ?? ""
      const bg = contentDiv?.style.backgroundColor ?? ""
      if (border || bg) {
        const parts = [
          bg && bg !== "transparent" ? `fill:${bg}` : "",
          border ? `border:${border.replace(/\s+/g, " ").trim()}` : "",
        ].filter(Boolean).join(", ")
        results.push({ type: "svg", x1, y1, x2, y2, desc: `rect(${x2 - x1}x${y2 - y1}) ${parts}`, ...frag })
        continue
      }
    }

    // Plain text
    const text = (el.textContent ?? "").replace(/\s+/g, " ").trim()
    if (text) results.push({ type: "text", x1, y1, x2, y2, text, ...frag })
  }

  return results
}

export async function parseSlideHtml(slideUrl: string): Promise<SlideElement[]> {
  let html: string
  try {
    const res = await fetch(slideUrl)
    if (!res.ok) return []
    html = await res.text()
  } catch {
    return []
  }

  const doc = new DOMParser().parseFromString(html, "text/html")
  const section = doc.querySelector(".reveal .slides section")
  if (!section) return []

  return extractElements(section)
}
